import React from "react";
import { FieldValues, FieldPath } from "react-hook-form";

import HookFormAutocomplete from "./inputs/HookFormAutocomplete";
import HookFormDatePicker from "./inputs/HookFormDatePicker";
import HookFormFileUpload from "./inputs/HookFormFileUpload";
import HookFormRadioGroup from "./inputs/HookFormRadioGroup";
import HookFormSelect from "./inputs/HookFormSelect";
import HookFormSwitch from "./inputs/HookFormSwitch";
import HookFormTextField from "./inputs/HookFormTextField";
import HookFormRating from "./inputs/HookFormRating";
import HookFormFileDropArea from "./inputs/HookFormFileDropArea";
import HookFormFileList from "./inputs/HookFormFileList";
import HookFormFileDropAreaWithFileList from "./inputs/HookFormFileDropAreaWithFileList";

import { useFormDisabled } from "./FormWideDisabledContext";
import { FileItem } from "types/form";
import { Dayjs } from "dayjs";

export type Option = {
    code: number | string;
    value: string;
    disabled?: boolean;
};

type BaseFieldProps<TFormValues extends FieldValues> = {
    /** The name of the field, used for form handling. */
    name: FieldPath<TFormValues>;
    /** The type of input to render. */
    input:
        | "textField"
        | "autocomplete"
        | "date"
        | "switch"
        | "select"
        | "radioGroup"
        | "file"
        | "fileDropAreaWithFileList"
        | "fileDropArea"
        | "fileList"
        | "rating";
    /** The type of the input field, applicable when input is "textField". */
    type?: string;
    /** Whether the field is disabled. */
    disabled?: boolean;
    /** Options for the autocomplete field, applicable when input is "autocomplete". */
    options?: Option[];
    /** Primarily used for select whether used empty option between select options. */
    includeEmptyOption?: boolean;
    /** Whether multiple options can be selected, applicable when input is "autocomplete". */
    multiple?: boolean;
    /** The label for the input field. */
    label?: string;
    /** Footnote for the input field. */
    footNote?: string;
    /** Additional onChange handler for the input field. */
    onChange?: (value?: any) => any;
    /** Number of rows for input fields. */
    rows?: number;
    /** Definition if the input field is multiline. */
    multiline?: boolean;
    /** Minimum date that can be selected for the date picker. */
    minDate?: Dayjs;
    /** Maximum date that can be selected for the date picker. */
    maxDate?: Dayjs;
    /** Default month that will be opened for date picker. */
    defaultCalendarMonth?: Dayjs;
    /** Normalization function for the input field. */
    normalize?: ((value: any) => any)[];
    /** Placeholder for the input field. */
    placeholder?: string;
    /** The direction of the radio group, applicable when input is "radioGroup". */
    direction?: "row" | "column";
    /** Whether the field is required. */
    required?: boolean;
    /** Whether options for the field are loading. */
    isLoading?: boolean;
    /** Maximum number of files that can be uploaded. */
    maxUploadedFiles?: number;
    /** Callback on when file is uploaded. */
    onLoadFile?: (file: FileItem) => Promise<Partial<FileItem>>;
    /** Callback on when file is deleted from file list. */
    onDeleteFile?: (file: FileItem) => Promise<void>;
    /** Children elements for the radio group. */
    children?: React.ReactNode;
};

export default function Field<TFormValues extends FieldValues>(props: BaseFieldProps<TFormValues>) {
    const { input, disabled, ...rest } = props;
    const isFormWideDisabled = useFormDisabled();

    const commonProps = {
        ...rest,
        disabled: disabled || isFormWideDisabled,
    };

    switch (input) {
        case "textField":
            return <HookFormTextField {...commonProps} />;
        case "autocomplete":
            return <HookFormAutocomplete {...commonProps} />;
        case "date":
            return <HookFormDatePicker {...commonProps} />;
        case "switch":
            return <HookFormSwitch {...commonProps} />;
        case "select":
            return <HookFormSelect {...commonProps} />;
        case "radioGroup":
            return <HookFormRadioGroup {...commonProps} />;
        case "file":
            return <HookFormFileUpload {...commonProps} />;
        case "fileDropAreaWithFileList":
            return <HookFormFileDropAreaWithFileList {...commonProps} />;
        case "fileDropArea":
            return <HookFormFileDropArea {...commonProps} />;
        case "fileList":
            return <HookFormFileList {...commonProps} />;
        case "rating":
            return <HookFormRating {...commonProps} />;
        default:
            return null;
    }
}
